import { color, font, table } from '../../shared/ui/tokens';

/**
 * The MUI theme's tables, split out of Theme.js so the table look has one owner.
 *
 * The CMS renders a list three different ways. Eleven screens use Bootstrap
 * `.table`, styled in main.css. Thirteen use MUI's own `Table`, either directly or
 * through `mui-datatables`. Two use `@mui/x-data-grid`. Left to their defaults the
 * MUI ones had 16px text, no header band, a 53px row of whatever height the content
 * wanted and MUI's divider colour, so the same list looked like a different product
 * depending on which screen you were on.
 *
 * Everything below is the same spec main.css applies to `.table`, read from the
 * same tokens. The numbers are in `shared/ui/tokens.js` and `css/tokens.css`, and
 * `tableUniformity.test.js` fails if the renderers drift apart.
 *
 * Redesign iteration 1 (2026-09-29) adds what the theme can do cheaply: body cells pad
 * 8px above and below so a row is 52 at least and grows with its content, right-aligned
 * cells use tabular figures and their header lines up with them, and a row takes the hover
 * tint while something in it has focus and the selected tint while it is selected.
 */
export const tableOverrides = {
  MuiTableContainer: {
    // MUI wraps tables in a Paper by default, which on a white page is a card inside a
    // card. The Bootstrap tables sit flat on the page; these do too.
    styleOverrides: { root: { backgroundColor: 'transparent', boxShadow: 'none' } },
  },
  MuiTable: {
    styleOverrides: { root: { borderCollapse: 'collapse' } },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        height: table.rowHeight,
        padding: `0 ${table.cellPadX}px`,
        fontSize: font.size.body,
        color: color.ink,
        borderBottom: `1px solid ${color.line}`,
        verticalAlign: 'middle',
      },
      head: {
        height: table.headHeight,
        backgroundColor: color.tableHeadBg,
        // --muted, as in main.css. The sorted column reads --ink: MUI's TableSortLabel
        // takes the primary text colour when it is active, which is --ink.
        color: color.muted,
        fontSize: font.size.caption,
        // 600, matching main.css. `font.weight.bold` is 700 and made every MUI header
        // a step heavier than every Bootstrap one, which is exactly the kind of
        // difference nobody spots on a single screen.
        fontWeight: font.weightWanted.semibold,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        borderTop: `1px solid ${color.borderLight}`,
        borderBottom: `1px solid ${color.borderLight}`,
      },
      // The height is the 52px floor and the padding is what a taller cell grows around,
      // as `.table > tbody > tr > td` does in main.css.
      body: {
        padding: `${table.cellPadY}px ${table.cellPadX}px`,
        // The first cell is the one you read, so it keeps body ink, as in main.css.
        '&:first-of-type': { color: color.ink },
        // mui-datatables' select box is a 42px button with its own inset, which fitted a
        // 52px row with nothing to spare; its cell keeps no vertical padding of its own.
        '&.MuiTableCell-paddingCheckbox': { paddingTop: 0, paddingBottom: 0 },
      },
      // A number column (`align="right"`): figures of one width, and a header that lines
      // up over them rather than being held to the left with the other labels.
      alignRight: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
      footer: { borderBottom: 0 },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      /* The same steps main.css gives a Bootstrap table: the zebra, then a hover dark
         enough to read on a striped row as well as a plain one, the same lift while the
         row holds focus, and the selected tint over all of them. Painted on the body cells
         rather than the row so a cell that sets its own background still wins, which is
         how the stylesheet does it too. */
      root: {
        '&:nth-of-type(even) > .MuiTableCell-body': {
          backgroundColor: color.tableStripeBg,
        },
        '&:hover > .MuiTableCell-body': { backgroundColor: color.rowHoverBg },
        '&:focus-within > .MuiTableCell-body': { backgroundColor: color.rowHoverBg },
        '&.Mui-selected > .MuiTableCell-body': { backgroundColor: color.rowSelectedBg },
      },
    },
  },
  /**
   * The data grid is not built out of `MuiTableCell`, so none of the above reaches it.
   * These are the same values again, against its own class names.
   */
  MuiDataGrid: {
    // The grid virtualises its rows, so it sizes them from props rather than from CSS;
    // a height in `styleOverrides.row` is ignored. Same two numbers as everything else.
    // That is also why its rows do not grow with their content the way the tables' do:
    // a virtualised row is a fixed height, and its cells keep to one line with an ellipsis.
    defaultProps: {
      rowHeight: table.rowHeight,
      columnHeaderHeight: table.headHeight,
      /* MUI X 6 tags no row as odd or even, so the zebra below had nothing to match and
         every grid was unstriped. The grid's own index on the page supplies the class, so
         the first row is white as on every other table. A screen that passes its own
         getRowClassName replaces this one, and that grid goes unstriped. */
      getRowClassName: ({ indexRelativeToCurrentPage }) =>
        indexRelativeToCurrentPage % 2 === 1 ? 'MuiDataGrid-row--even' : '',
    },
    styleOverrides: {
      root: {
        border: 0,
        fontFamily: font.family,
        fontSize: font.size.body,
        color: color.ink,
      },
      columnHeaders: {
        minHeight: table.headHeight,
        maxHeight: table.headHeight,
        backgroundColor: color.tableHeadBg,
        borderTop: `1px solid ${color.borderLight}`,
        borderBottom: `1px solid ${color.borderLight}`,
        borderRadius: 0,
      },
      columnHeaderTitle: {
        color: color.muted,
        fontSize: font.size.caption,
        fontWeight: font.weightWanted.semibold,
      },
      columnHeader: {
        padding: `0 ${table.cellPadX}px`,
        // Not the select-all checkbox's header. Its column is the grid's own narrow one,
        // and the side inset pushed the checkbox off it (a sliver on Manage Assets); the
        // grid centres it in the column with no inset, as it does the row checkboxes.
        '&.MuiDataGrid-columnHeaderCheckbox': { padding: 0 },
        // The sorted column's label reads --ink, as `th[aria-sort]` does in main.css.
        '&.MuiDataGrid-columnHeader--sorted .MuiDataGrid-columnHeaderTitle': {
          color: color.ink,
        },
      },
      cell: {
        padding: `0 ${table.cellPadX}px`,
        borderBottom: `1px solid ${color.line}`,
        '&:focus, &:focus-within': { outline: 'none' },
        // A `type: 'number'` column is right-aligned by the grid; its figures are one width.
        '&.MuiDataGrid-cell--textRight': { fontVariantNumeric: 'tabular-nums' },
      },
      // The grid virtualises, so it cannot use `:nth-of-type`: a row's DOM position is
      // its position in the window, not in the data. It tags the real index instead.
      row: {
        '&.MuiDataGrid-row--even': { backgroundColor: color.tableStripeBg },
        '&:hover': { backgroundColor: color.rowHoverBg },
        // The stripe above is the more specific selector, so the hover needs to match
        // it to win on an even row.
        '&.MuiDataGrid-row--even:hover': { backgroundColor: color.rowHoverBg },
        // Selected wins over both, so it comes last at the same weight.
        '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: color.rowSelectedBg },
      },
      footerContainer: { borderTop: `1px solid ${color.borderLight}` },
    },
  },
};

export default tableOverrides;
